/** Sync Zustand app-store preferences to DB via /api/preferences — debounced writes, load on mount */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useAuth } from './use-auth'
import { apiClient } from '../lib/api-client'
import { useAppStore, type Tab } from '../stores/app-store'

/** Debounce delay for saving preferences to DB (ms) */
const SAVE_DELAY = 1500

/** Load all preferences from DB and merge into Zustand store */
async function loadPreferences(): Promise<void> {
  try {
    const { preferences: prefs } = await apiClient.get<{ preferences: Record<string, string> }>('/api/preferences')

    const store = useAppStore.getState()

    // Union-merge expanded folders from DB with local state to avoid losing unsaved changes.
    // Note: this is append-only — collapsing a folder on one device won't collapse it on another.
    // A "DB wins" strategy would be cleaner for cross-device sync but risks losing local toggles.
    if (prefs.expanded_folders) {
      try {
        const dbFolders: string[] = JSON.parse(prefs.expanded_folders)
        if (Array.isArray(dbFolders)) {
          const merged = [...new Set([...store.expandedFolderIds, ...dbFolders])]
          if (merged.length !== store.expandedFolderIds.length) {
            useAppStore.setState({ expandedFolderIds: merged })
          }
        }
      } catch { /* ignore malformed JSON */ }
    }

    // Merge open tabs from DB (only if local tabs are empty — avoids overwriting active session)
    if (prefs.open_tabs && store.openTabs.length === 0) {
      try {
        const dbTabs: Omit<Tab, 'isDirty'>[] = JSON.parse(prefs.open_tabs)
        if (Array.isArray(dbTabs) && dbTabs.length > 0) {
          useAppStore.setState({
            openTabs: dbTabs.map((t) => ({ id: t.id, documentId: t.documentId, title: t.title, isDirty: false })),
          })
        }
      } catch { /* ignore */ }
    }

    if (prefs.active_tab && !store.activeTabId) {
      useAppStore.setState({ activeTabId: prefs.active_tab })
    }
  } catch {
    // Silently fail — preferences are non-critical
  }
}

/** Save a single preference key to DB */
async function savePreference(key: string, value: string): Promise<void> {
  try {
    await apiClient.put(`/api/preferences/${key}`, { value })
  } catch {
    // Silently fail — will retry on next change
  }
}

/**
 * Hook that syncs expanded folder IDs and open tabs to the database.
 * Call once at app root level (e.g., in layout component).
 */
export function usePreferenceSync(): void {
  const { user } = useAuth()
  const [loaded, setLoaded] = useState(false)
  const loadTriggeredRef = useRef(false)
  const foldersTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tabsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load preferences from DB on first auth — set loaded=true only AFTER async completes
  useEffect(() => {
    if (!user || loadTriggeredRef.current) return
    loadTriggeredRef.current = true
    loadPreferences().then(() => setLoaded(true)).catch(() => setLoaded(true))
  }, [user])

  // Debounced save for expanded folders
  const debouncedSaveFolders = useCallback((folderIds: string[]) => {
    if (foldersTimerRef.current) clearTimeout(foldersTimerRef.current)
    foldersTimerRef.current = setTimeout(() => {
      void savePreference('expanded_folders', JSON.stringify(folderIds))
    }, SAVE_DELAY)
  }, [])

  // Debounced save for open tabs
  const debouncedSaveTabs = useCallback((tabs: Tab[], activeTabId: string | null) => {
    if (tabsTimerRef.current) clearTimeout(tabsTimerRef.current)
    tabsTimerRef.current = setTimeout(() => {
      const cleanTabs = tabs.map(({ isDirty: _, ...rest }) => rest)
      void savePreference('open_tabs', JSON.stringify(cleanTabs))
      if (activeTabId) void savePreference('active_tab', activeTabId)
    }, SAVE_DELAY)
  }, [])

  // Subscribe to store changes and sync to DB — only after initial load completes
  useEffect(() => {
    if (!user || !loaded) return

    let prevFolders = useAppStore.getState().expandedFolderIds
    let prevTabs = useAppStore.getState().openTabs
    let prevActiveTab = useAppStore.getState().activeTabId

    const unsub = useAppStore.subscribe((state) => {
      // Sync expanded folders
      if (state.expandedFolderIds !== prevFolders) {
        prevFolders = state.expandedFolderIds
        debouncedSaveFolders(state.expandedFolderIds)
      }

      // Sync open tabs
      if (state.openTabs !== prevTabs || state.activeTabId !== prevActiveTab) {
        prevTabs = state.openTabs
        prevActiveTab = state.activeTabId
        debouncedSaveTabs(state.openTabs, state.activeTabId)
      }
    })

    return () => {
      unsub()
      if (foldersTimerRef.current) clearTimeout(foldersTimerRef.current)
      if (tabsTimerRef.current) clearTimeout(tabsTimerRef.current)
    }
  }, [user, loaded, debouncedSaveFolders, debouncedSaveTabs])
}
